import { Subject } from 'rxjs'
import { debounceTime } from 'rxjs/operators'

interface InvokeContext {
  args: any[]
  context: any
}

/**
 * 调用节流方法装饰器，默认时间为 10ms
 * 所有调用（含首次）统一走 debounce：同一静默窗口内多次触发合并为一次执行，
 * 窗口内挂起的 Promise 共享同一结果（以最后一次调用的参数为准）。
 * 避免「首调直出、紧接再入队」在页面打开时打出两次相同列表请求。
 * @param time 节流等待时间
 * @constructor
 */
// eslint-disable-next-line no-magic-numbers
export function InvokeThrottle (time = 10): MethodDecorator {
  return function (target, propertyKey, descriptor) {
    const rawMethod = descriptor.value as any

    const subject = new Subject<InvokeContext>()
    const pendingPairs: Array<{ resolve: (v: any) => void; reject: (e: any) => void }> = []

    subject.asObservable().pipe(debounceTime(time)).subscribe((context: InvokeContext) => {
      let exec: any
      try {
        exec = rawMethod.apply(context.context, context.args)
      } catch (e) {
        while (pendingPairs.length) {
          const { reject } = pendingPairs.shift()!
          reject(e)
        }
        return
      }
      Promise.resolve(exec).then(
        (result) => {
          while (pendingPairs.length) {
            const { resolve } = pendingPairs.shift()!
            resolve(result)
          }
        },
        (err) => {
          while (pendingPairs.length) {
            const { reject } = pendingPairs.shift()!
            reject(err)
          }
        }
      )
    })

    descriptor.value = function (this: any, ...args: any[]) {
      return new Promise((resolve, reject) => {
        pendingPairs.push({ resolve, reject })
        subject.next({
          args,
          context: this
        })
      })
    } as any
  }
}
